import {
  ACCESS_AUTHORIZATION_RESPONSE_LABEL,
  ACTIVITY_TYPE_LABEL,
  AVAILABILITY_ANSWER_LABEL,
  LEAD_SOURCE_LABEL,
  PROPOSAL_STATUS_LABEL,
  RETURN_ANSWER_LABEL,
  SHARE_INVITATION_STATUS_LABEL,
  type StoredNotification,
} from "../types";

/** Texto curto exibido na lista/painel. */
export function notificationTitle(n: StoredNotification): string {
  switch (n.type) {
    case "NewLead":
      return `Novo lead: ${n.payload.personName}`;
    case "ProposalReceived":
      return `Nova proposta recebida (v${n.payload.version})`;
    case "ProposalStatusChanged":
      return `Proposta ${PROPOSAL_STATUS_LABEL[n.payload.status]}`;
    case "ShareInvitationReceived":
      return `${n.payload.ownerBrokerName} compartilhou um imóvel com você`;
    case "ShareInvitationResponded":
      return `Convite ${SHARE_INVITATION_STATUS_LABEL[n.payload.status]}`;
    case "ShareRevoked":
      return "Compartilhamento de imóvel revogado";
    case "ActivityReminder":
      return `Lembrete: ${ACTIVITY_TYPE_LABEL[n.payload.activityType]} com ${n.payload.personName}`;
    case "PaymentConfirmed":
      return "Pagamento confirmado";
    case "SupportTicketReplied":
      return "Suporte respondeu seu chamado";
    case "InspectionReportReady":
      return "Laudo de vistoria pronto";
    case "ContractMinutaReady":
      return "Minuta do contrato pronta";
    case "PartnerInviteReceived":
      return `${n.payload.inviterName} quer te adicionar como parceiro`;
    case "PartnerInviteResponded":
      return `Convite de parceria ${n.payload.status === "accepted" ? "aceito" : "recusado"}`;
    case "AccessAuthorizationResponded":
      return `Acesso ao imóvel ${ACCESS_AUTHORIZATION_RESPONSE_LABEL[n.payload.status]}`;
    case "PropertyAvailabilityResponded":
      return `Resposta do proprietário — ${n.payload.propertyTitle}`;
    case "PropertyReturnResponded":
      return n.payload.source === "reminder"
        ? `Imóvel deve estar pronto — ${n.payload.propertyTitle}`
        : `O proprietário decidiu — ${n.payload.propertyTitle}`;
    case "OwnerWelcomeViewed":
      return `${n.payload.ownerName ?? "O proprietário"} abriu suas boas-vindas`;
    case "DemandBroadcastReceived":
      return "Um parceiro procura um imóvel";
    // O assunto tem de dizer O QUE É sem abrir: o corretor recebe convite de
    // parceria e de lançamento pela mesma notificação, e precisa distinguir.
    case "LaunchInviteReceived":
      return n.payload.launchId
        ? `Novo lançamento: ${n.payload.launchName}`
        : `Convite de parceria: ${n.payload.developerName}`;
  }
}

/** Linha secundária com detalhes (ex: imóvel referenciado). */
export function notificationSubtitle(n: StoredNotification): string | null {
  switch (n.type) {
    case "NewLead":
      return `Origem: ${LEAD_SOURCE_LABEL[n.payload.source]}`;
    case "ActivityReminder":
      return n.payload.propertyTitle;
    case "ShareInvitationReceived":
      return `Comissão ${n.payload.commissionPercentage}%`;
    case "PaymentConfirmed":
      return `R$ ${Number(n.payload.amount).toFixed(2).replace(".", ",")}`;
    case "SupportTicketReplied":
      return `${n.payload.code} · ${n.payload.subject}`;
    case "InspectionReportReady":
      return n.payload.propertyTitle;
    case "ContractMinutaReady":
      return n.payload.internalCode;
    case "PartnerInviteReceived":
      return n.payload.inviterPublicCode ?? null;
    case "AccessAuthorizationResponded":
      return n.payload.propertyTitle;
    case "PropertyAvailabilityResponded": {
      const label = AVAILABILITY_ANSWER_LABEL[n.payload.answer];
      const message = n.payload.message?.trim();
      return message ? `${label} — "${message}"` : label;
    }
    case "PropertyReturnResponded": {
      if (n.payload.source === "reminder") {
        return "Chegou a data que o dono deu para o imóvel ficar pronto.";
      }
      const label = RETURN_ANSWER_LABEL[n.payload.answer];
      const message = n.payload.message?.trim();
      return message ? `${label} — "${message}"` : label;
    }
    case "OwnerWelcomeViewed":
      return n.payload.propertyTitle;
    case "DemandBroadcastReceived": {
      const region = [n.payload.city, n.payload.uf].filter(Boolean).join("/");
      return region || n.payload.note || null;
    }
    case "LaunchInviteReceived":
      return n.payload.launchId ? n.payload.developerName : null;
    default:
      return null;
  }
}

/** Rota associada à notificação. */
export function notificationRoute(n: StoredNotification): string {
  switch (n.type) {
    case "NewLead":
      return `/leads/${n.payload.id}`;
    case "ProposalReceived":
      return `/proposals/${n.payload.proposalGroupId}`;
    case "ProposalStatusChanged":
      return `/proposals/${n.payload.proposalGroupId}`;
    case "ShareInvitationReceived":
    case "ShareInvitationResponded":
      return "/cobroking";
    case "ShareRevoked":
      return "/properties";
    case "ActivityReminder":
      return `/agenda?activity=${n.payload.id}`;
    case "PaymentConfirmed":
      return "/wallet";
    case "SupportTicketReplied":
      return `/support/${n.payload.ticketId}`;
    case "InspectionReportReady":
      return `/inspections/${n.payload.inspectionId}`;
    case "ContractMinutaReady":
      return `/contracts/${n.payload.contractId}`;
    case "PartnerInviteReceived":
    case "PartnerInviteResponded":
      return "/parceiros";
    case "AccessAuthorizationResponded":
      return `/properties/${n.payload.propertyId}`;
    case "PropertyAvailabilityResponded":
      return `/properties/${n.payload.propertyId}`;
    case "PropertyReturnResponded":
      return `/properties/${n.payload.propertyId}`;
    case "OwnerWelcomeViewed":
      return `/properties/${n.payload.propertyId}`;
    case "DemandBroadcastReceived":
      return "/demand-requests";
    // Vai para a lista, não para `/launches/{id}`: o convite ainda está PENDENTE e o
    // detalhe é bloqueado até o aceite — o link levaria a um 404. É em /launches que
    // ficam a caixa de convites e os botões de aceitar e recusar.
    case "LaunchInviteReceived":
      return "/launches";
  }
}
